import { html, nothing } from "lit";
import { isSettingsNavigationRoute, isSettingsTakeover } from "../app-navigation.ts";
import { isSessionRouteId } from "../app-route-paths.ts";
import { isRouteId, type RouteId } from "../app-routes.ts";
import { renderGatewayStatus } from "../components/gateway-status.ts";
import { icons } from "../components/icons.ts";
import { renderConnectingSplash } from "../components/loading-skeleton.ts";
import { renderNewSessionLink } from "../components/new-session-link.ts";
import { renderLazySettingsSidebar } from "../components/settings-sidebar-lazy.ts";
import type { ThemeModeChangeDetail } from "../components/theme-mode-toggle.ts";
import { t } from "../i18n/index.ts";
import { canCallGatewayMethod } from "../lib/gateway-methods.ts";
import { resolveGatewayStatus } from "../lib/gateway-status.ts";
import {
  formatKeyboardShortcutCombo,
  KEYBOARD_SHORTCUT_COMBOS,
} from "../lib/keyboard-shortcut-contract.ts";
import { readSessionMethodAccess } from "../lib/session-method-access.ts";
import { normalizeAgentId, resolveUiSelectedSessionAgentId } from "../lib/sessions/session-key.ts";
import { isTerminalAvailable } from "../lib/terminal-availability.ts";
import type { NewSessionTarget } from "../pages/new-session/location.ts";
import { pluginTabKey, pluginTabRefFromSearch } from "../pages/plugin/route.ts";
import { renderPluginSurface } from "../plugins/control-ui-view.ts";
import type { ShellRouteState } from "./app-host-route-state.ts";
import {
  renderLazyDevicePairSetup,
  type DevicePairSetupHost,
} from "./app-shell-device-pair-setup.ts";
import { renderShellDocks } from "./app-shell-docks.ts";
import type { OutboxStoreRuntime, StoredOutboxScopeHost } from "./app-shell-gateway.ts";
import { renderShellLazyOverlays, type ShellLazyOverlayHost } from "./app-shell-lazy-view.ts";
import type { ApplicationRuntime } from "./bootstrap.ts";
import { canGoBackInNativeEmbed } from "./browser.ts";
import type { ApplicationContext, ApplicationNavigationOptions } from "./context.ts";
import { gatewayPresentationScope } from "./gateway-presentation-scope.ts";
import {
  APP_SIDEBAR_ELEMENT,
  isOptionalElementDefined,
  MACOS_TITLEBAR_ELEMENT,
  type OptionalCustomElement,
  SIDEBAR_ATTENTION_ELEMENT,
} from "./lazy-custom-element.ts";
import { isMobileNavLayout, shouldMergeChatChrome } from "./mobile-nav-layout.ts";
import type { NativeHistoryState } from "./native-web-chrome.ts";
import { isNativeEmbedHost, isNativeWebChromeHost } from "./native-web-chrome.ts";
import { beginNativeWindowDragFromTopInset } from "./native-window-drag.ts";
import {
  floatingSidebarAttentionVisible,
  navigationSurfaceIsHidden,
  renderFloatingUpdateCard,
} from "./navigation-surface.ts";
import { readGatewayOperatorAccess } from "./operator-access.ts";
import { isDesktopPanelAvailable, isHomePanelAvailable } from "./panel-availability.ts";
import { NAV_WIDTH_MAX, NAV_WIDTH_MIN, normalizeCatalogOpenTarget } from "./settings.ts";
import { renderCollapsedHomeToggle } from "./shell-assistant-toggles.ts";
import { createUpdateProgressWatcher } from "./update-confirmation.ts";

const EMPTY_SESSION_HAS_DRAFT = () => false;

type SettingsSidebarHost = Parameters<typeof renderLazySettingsSidebar>[0];

export interface ShellViewHost
  extends DevicePairSetupHost, ShellLazyOverlayHost, SettingsSidebarHost {
  readonly runtime: ApplicationRuntime | undefined;
  readonly activeSessionKey: string;
  readonly custodianMinimizeRequestId: number;
  readonly desktopNavigationExpanded: boolean;
  readonly execApprovalElement: OptionalCustomElement;
  readonly onboardingMemoryImportElement: OptionalCustomElement;
  readonly nativeHistoryState: NativeHistoryState;
  readonly navDrawerOpen: boolean;
  readonly navigationSidebar: HTMLElement;
  readonly onboardingMode: boolean;
  readonly outboxStoreRuntime: OutboxStoreRuntime | null;
  readonly routeState: ShellRouteState;
  readonly settingsPreloadTimers: Map<EventTarget, ReturnType<typeof globalThis.setTimeout>>;
  readonly settingsSearchQuery: string;
  closeNavDrawer(options?: { restoreFocus?: boolean }): void;
  newSessionRouteAgentId(): string;
  enabledRouteIds(): readonly RouteId[];
  exitSettings(): void;
  handleNativeNewSession(): void;
  handleSettingsSearchQueryChange(query: string): Promise<void>;
  handleThemeChange(event: CustomEvent<ThemeModeChangeDetail>): void;
  nativeNavCollapsed(): boolean;
  openApprovals(): void;
  openNewSession(agentId: string, target?: NewSessionTarget): void;
  openPalette(): void;
  refreshControlUi: () => Promise<boolean>;
  recoverNotFoundRoute: () => boolean;
  requestUpdate(): void;
  resizeNavigation(splitRatio: number): void;
  storedOutboxScopeHost(context: ApplicationContext): StoredOutboxScopeHost;
  toggleNavigationSurface(trigger?: HTMLElement): void;
}

export function renderApplicationShell(host: ShellViewHost) {
  const context = host.context;
  const runtime = host.runtime;
  if (!context || !runtime) {
    return nothing;
  }
  if (host.routeState.routeId === undefined && !host.routeState.routeFailed) {
    return renderConnectingSplash();
  }
  const gatewaySnapshot = context.gateway.snapshot;
  const config = context.config.current;
  const gatewayConnected = gatewaySnapshot.phase === "connected";
  const operatorAccess = readGatewayOperatorAccess(gatewaySnapshot);
  const canUpdate = canCallGatewayMethod(gatewaySnapshot, "update.run", "operator.admin");
  const canHoldUpdate =
    canUpdate && canCallGatewayMethod(gatewaySnapshot, "update.hold", "operator.admin");
  const outboxScopeHost = host.storedOutboxScopeHost(context);
  const storedOutboxes = host.outboxStoreRuntime?.summarizeStoredChatOutboxes(outboxScopeHost);
  const navigationSnapshot = context.navigation.snapshot;
  const overlaySnapshot = context.overlays.snapshot;
  const controlUiRefreshRequired = overlaySnapshot.controlUiRefreshRequired;
  const connectionStatus = resolveGatewayStatus(gatewaySnapshot, controlUiRefreshRequired);
  const presentationScope = gatewayPresentationScope(context.gateway);
  // Initial hello can paint the shell before recovery finishes. Keep that brief
  // startup state in existing chrome rather than inserting and removing a row.
  const initialConnection =
    !presentationScope.readyOnce &&
    !gatewaySnapshot.offlineStable &&
    (connectionStatus === "connecting" ||
      connectionStatus === "starting" ||
      connectionStatus === "restoring");
  // The install keeps running after `update.run` answers, so the reconciliation
  // — not the request — decides how long the update surfaces stay busy.
  const updateBusy = overlaySnapshot.updateRunning || overlaySnapshot.updateReconciliationPending;
  const watchUpdateProgress = createUpdateProgressWatcher(context);
  const terminalAvailable = isTerminalAvailable(gatewaySnapshot, config.terminalEnabled ?? false);
  const desktopPanelAvailable = isDesktopPanelAvailable(gatewaySnapshot);
  const homePanelAvailable = isHomePanelAvailable(context.gateway);
  const custodianPanelAvailable =
    // Scope-aware to match the store: admin-only, never advertisement alone.
    canCallGatewayMethod(gatewaySnapshot, "openclaw.chat", "operator.admin");
  const activeRoute = host.routeState.routeId ?? "chat";
  const sessionRoute = isSessionRouteId(activeRoute);
  // Session routes have an offline outbox, New Session keeps a local draft, and
  // Appearance persists local preference intent for replay. Connection settings
  // must remain usable to replace an unreachable Gateway. Their server actions
  // are independently gated; other pages cannot submit useful disconnected work.
  const reloadRequired = gatewaySnapshot.phase === "reload-required";
  const pageActionsBlocked =
    !reloadRequired &&
    !gatewayConnected &&
    !sessionRoute &&
    activeRoute !== "new-session" &&
    activeRoute !== "appearance" &&
    activeRoute !== "connection";
  // Plugin tabs share one route; the URL picks the active item.
  const activePluginRef =
    activeRoute === "plugin"
      ? pluginTabRefFromSearch(
          host.routeState.location?.search ?? "",
          host.routeState.location?.pathname,
          context.basePath,
        )
      : null;
  // Onboarding renders without any navigation chrome, so the settings takeover
  // must not reserve its fixed sidebar column (the grid would stay off-center).
  const nativeEmbed = isNativeEmbedHost();
  const embedSettingsRoot = nativeEmbed && activeRoute === "settings";
  const embedSettings =
    nativeEmbed &&
    (activeRoute === "settings" ||
      isSettingsNavigationRoute(activeRoute) ||
      activeRoute === "skills" ||
      activeRoute === "cron");
  const settingsTakeover = isSettingsTakeover(activeRoute) && !host.onboardingMode && !nativeEmbed;
  const runtimeConfig = context.runtimeConfig.state;
  const onboarding = host.onboardingMode;
  const memoryImportActive = onboarding && activeRoute !== "custodian";
  host.lazyCustomElements.requestWhileActive(
    host.onboardingMemoryImportElement,
    memoryImportActive,
  );
  const navDrawerOpen = host.navDrawerOpen && !onboarding && !nativeEmbed;
  const mobileNavLayout = isMobileNavLayout();
  const nativeWebChrome = isNativeWebChromeHost() && !nativeEmbed;
  // Native chrome is absent in browsers; the shell owns visible retry if its chunk fails.
  if (nativeWebChrome && !onboarding) {
    host.lazyCustomElements.preload(MACOS_TITLEBAR_ELEMENT, { reportError: true });
  }
  const mergedChatChrome = shouldMergeChatChrome({
    mobileNavLayout,
    routeId: activeRoute,
    onboarding,
  });
  // Drawer navigation always opens expanded; the tab's desktop collapse state
  // stays in memory for when the viewport returns to the desktop layout.
  // The settings sidebar has a fixed width, so the collapse state pauses too.
  const navCollapsed =
    !nativeEmbed &&
    navigationSnapshot.navCollapsed &&
    !host.desktopNavigationExpanded &&
    !navDrawerOpen &&
    !settingsTakeover;
  const navigationSurfaceHidden = navigationSurfaceIsHidden({
    onboarding,
    navCollapsed,
    navDrawerOpen,
    mobileNavLayout,
  });
  const floatingAttentionVisible =
    !nativeEmbed &&
    floatingSidebarAttentionVisible({
      navigationSurfaceHidden,
      mobileNavLayout,
      onboarding,
      compact: mergedChatChrome,
    });
  if (!nativeEmbed && (onboarding || floatingAttentionVisible)) {
    host.lazyCustomElements.preload(SIDEBAR_ATTENTION_ELEMENT, { reportError: true });
  }
  const shellWidth = Math.max(globalThis.innerWidth || 0, NAV_WIDTH_MAX);
  // A route query is navigation input, not an owner record. Let it override the
  // live selection only after the roster proves that agent exists.
  const requestedRouteAgentId = host.newSessionRouteAgentId();
  const routeAgentId = requestedRouteAgentId ? normalizeAgentId(requestedRouteAgentId) : null;
  const routeAgentIsKnown =
    routeAgentId !== null &&
    context.agents.state.agentsList?.agents.some(
      (agent) => normalizeAgentId(agent.id) === routeAgentId,
    ) === true;
  const selectedAgentId = routeAgentIsKnown
    ? routeAgentId
    : normalizeAgentId(context.agentSelection.state.selectedId ?? gatewaySnapshot.assistantAgentId);
  const newSessionAccess = readSessionMethodAccess(gatewaySnapshot, {
    method: "sessions.create",
    params: {},
  });
  const openNewSession = (agentId: string, target?: NewSessionTarget) => {
    const access = readSessionMethodAccess(context.gateway.snapshot, {
      method: "sessions.create",
      params: {},
    });
    if (access.allowed) {
      host.openNewSession(agentId, target);
    }
  };
  const uiSettings = context.theme.settings;
  // The new-session draft shares the chat layout: full-height pane that owns
  // its scrolling and pins the composer dock to the bottom.
  const chatLikeRoute = sessionRoute || activeRoute === "new-session" || activeRoute === "systems";
  if (!settingsTakeover && !nativeEmbed) {
    Object.assign(host.navigationSidebar, {
      basePath: context.basePath,
      activeRouteId: activeRoute,
      router: host.runtime.router,
      activePluginTabId: activePluginRef ? pluginTabKey(activePluginRef) : "",
      enabledRouteIds: host.enabledRouteIds(),
      sessionKey: host.activeSessionKey,
      connected: gatewayConnected,
      connectionStatus,
      lastError: gatewaySnapshot.lastError,
      outboxAttentionCountForSession: storedOutboxes?.attentionCountForSession ?? (() => 0),
      hasSessionDraft: storedOutboxes?.hasSessionDraft ?? EMPTY_SESSION_HAS_DRAFT,
      terminalAvailable,
      catalogOpenTarget: normalizeCatalogOpenTarget(uiSettings.catalogOpenTarget),
      canPairDevice: gatewayConnected && (operatorAccess.canAdmin || operatorAccess.canPair),
      preferencesBrowserOnly: gatewayConnected && context.runtimeConfig.canPatch === false,
      sidebarEntries: navigationSnapshot.sidebarEntries,
      navigationVisible: !navigationSurfaceHidden,
      sidebarAgentsMode: uiSettings.sidebarAgentsMode ?? "chip",
      sidebarLiveActivity: uiSettings.sidebarLiveActivity !== false,
      pinnedAgentIds: navigationSnapshot.pinnedAgentIds,
      themeMode: context.theme.mode,
      gatewayVersion: config.serverVersion ?? gatewaySnapshot.hello?.server?.version ?? null,
      devGitBranch: config.devGitBranch,
      watchUpdateProgress,
      onOpenPalette: () => host.openPalette(),
      onRetryConnect: () => context.gateway.connect(),
      onToggleSidebar: () => host.toggleNavigationSurface(),
      onOpenNewSession: openNewSession,
      onUpdateSidebarEntries: (entries: string[]) =>
        context.navigation.update({ sidebarEntries: entries }),
      onPairMobile: () => void context.overlays.openDevicePairSetup(),
      onNavigate: (routeId: string, options?: ApplicationNavigationOptions) =>
        host.navigate(routeId, options),
      onPreloadRoute: (routeId: string) =>
        isRouteId(routeId) ? context.preload(routeId) : Promise.resolve(),
    });
  }
  const navigationContent =
    settingsTakeover || nativeEmbed
      ? renderLazySettingsSidebar(host, {
          presentation: nativeEmbed ? (embedSettingsRoot ? "embed-list" : "embed-page") : "sidebar",
          basePath: context.basePath,
          activeRouteId: activeRoute,
          agents: context.agents.state.agentsList?.agents ?? [],
          agentIdentity: context.agentIdentity,
          settingsAgentSelection: context.settingsAgentSelection,
          activePathname: host.routeState.location?.pathname ?? "",
          activeSearch: host.routeState.location?.search ?? "",
          activeHash: host.routeState.location?.hash ?? "",
          connectionStatus,
          lastError: gatewaySnapshot.lastError,
          gatewayVersion: config.serverVersion ?? gatewaySnapshot.hello?.server?.version ?? "",
          searchQuery: embedSettingsRoot ? "" : host.settingsSearchQuery,
          searchParams: {
            query: host.settingsSearchQuery,
            schema: runtimeConfig.configSchema,
            value: runtimeConfig.configForm ?? runtimeConfig.configSnapshot?.config ?? null,
            uiHints: runtimeConfig.configUiHints,
            identityAvailable: Boolean(gatewaySnapshot.selfUser),
            basePath: context.basePath,
            canAdmin: operatorAccess.canAdmin,
            nativeDeviceSettings: context.nativeDeviceSettings,
          },
          onExit: () => {
            if (!nativeEmbed) {
              host.exitSettings();
            } else if (canGoBackInNativeEmbed()) {
              window.history.back();
            } else if (activeRoute === "memory-import") {
              context.replace("memory");
            } else {
              host.navigate("settings");
            }
          },
          onRetryConnect: () => context.gateway.connect(),
          onNavigate: (routeId, options) => host.navigate(routeId, options),
          onPreload: (routeId) => context.preload(routeId),
          onSearchQueryChange: (nextQuery) => void host.handleSettingsSearchQueryChange(nextQuery),
          preloadTimers: host.settingsPreloadTimers,
          saveIndicator: {
            status: runtimeConfig.configRecoveryError
              ? "recovery"
              : runtimeConfig.configAutoSaveStatus,
            lastError: runtimeConfig.configRecoveryError ?? runtimeConfig.lastError,
            needsApply: runtimeConfig.configNeedsApply,
            applying: runtimeConfig.configApplying,
            applyDisabled:
              context.runtimeConfig.canApply === false ||
              runtimeConfig.configLoading ||
              runtimeConfig.configSaving ||
              (runtimeConfig.configFormDirty && runtimeConfig.configFormMode === "raw") ||
              updateBusy,
            onRetry: () => void context.runtimeConfig.retry(),
            onSave: () => void context.runtimeConfig.save(),
            onReload: () => void context.runtimeConfig.discardDraft(),
            onApply: () => void context.runtimeConfig.apply(),
          },
          canAdmin: operatorAccess.canAdmin,
          nativeDeviceSettings: context.nativeDeviceSettings,
        })
      : host.navigationSidebar;
  // Optional tags stay mounted before definition. Lit replays their properties on upgrade,
  // and the upgraded panels catch the first toggle instead of dropping the event.
  const workspace = html`
    ${renderShellLazyOverlays(host, desktopPanelAvailable, custodianPanelAvailable, nativeEmbed)}
    <div
      class="shell ${chatLikeRoute ? "shell--chat" : ""} ${
        navCollapsed ? "shell--nav-collapsed" : ""
      } ${mobileNavLayout ? "shell--mobile-nav" : ""} ${
        mergedChatChrome ? "shell--merged-chat-chrome" : ""
      } ${navDrawerOpen ? "shell--nav-drawer-open" : ""} ${
        onboarding ? "shell--onboarding" : ""
      } ${nativeEmbed ? "shell--embed" : ""} ${embedSettings ? "shell--embed-settings" : ""} ${settingsTakeover ? "shell--settings" : ""}"
      style=${`--shell-nav-expanded-width: ${navigationSnapshot.navWidth}px`}
      @theme-change=${(event: CustomEvent<ThemeModeChangeDetail>) => host.handleThemeChange(event)}
    >
      <a class="shell-skip-link" href="#control-ui-main" ?inert=${navDrawerOpen}>
        ${t("common.skipToMainContent")}
      </a>
      ${
        nativeWebChrome && !onboarding
          ? html`
              <openclaw-macos-titlebar-controls
                ?inert=${navDrawerOpen}
                .navCollapsed=${host.nativeNavCollapsed()}
                .historyOnly=${settingsTakeover}
                .canGoBack=${host.nativeHistoryState.canGoBack}
                .canGoForward=${host.nativeHistoryState.canGoForward}
                .newSessionDisabledReason=${
                  newSessionAccess.allowed ? undefined : newSessionAccess.reason
                }
                .onToggleSidebar=${() => host.toggleNavigationSurface()}
                .onOpenPalette=${() => host.openPalette()}
                .onOpenNewSession=${() => host.handleNativeNewSession()}
              ></openclaw-macos-titlebar-controls>
            `
          : nothing
      }
      ${
        nativeEmbed
          ? nothing
          : html`<openclaw-app-topbar
              ?inert=${navDrawerOpen}
              .resourceBasePath=${context.resourceBasePath}
              .environment=${config.environment}
              .navDrawerOpen=${navDrawerOpen}
              .onOpenPalette=${() => host.openPalette()}
              .onToggleDrawer=${(trigger: HTMLElement) => host.toggleNavigationSurface(trigger)}
            ></openclaw-app-topbar>`
      }
      ${
        !nativeEmbed && navCollapsed && !onboarding && !settingsTakeover && !mobileNavLayout
          ? html`
              <div class="shell-chrome-controls">
                <openclaw-tooltip
                  .content=${`${t("nav.expand")} (${formatKeyboardShortcutCombo(KEYBOARD_SHORTCUT_COMBOS.toggleSidebar)})`}
                >
                  <button
                    type="button"
                    class="shell-chrome-controls__button shell-chrome-controls__nav-toggle"
                    aria-label=${t("nav.expand")}
                    aria-expanded="false"
                    data-env-avatar=${
                      config.environment ? config.assistantIdentity.name.charAt(0) : nothing
                    }
                    @click=${() => host.toggleNavigationSurface()}
                  >
                    ${icons.panelLeftOpen}
                  </button>
                </openclaw-tooltip>
                ${renderNewSessionLink({
                  basePath: context.basePath,
                  agentId: selectedAgentId,
                  className: "shell-chrome-controls__button shell-chrome-controls__new-thread",
                  label: t("chat.runControls.newSession"),
                  showShortcut: true,
                  disabledReason: newSessionAccess.allowed ? undefined : newSessionAccess.reason,
                  onOpen: openNewSession,
                })}
                <openclaw-tooltip
                  .content=${`${t("chat.openCommandPalette")} (${formatKeyboardShortcutCombo(KEYBOARD_SHORTCUT_COMBOS.commandPalette)})`}
                >
                  <button
                    type="button"
                    class="shell-chrome-controls__button shell-chrome-controls__search"
                    aria-label=${t("chat.openCommandPalette")}
                    @click=${() => host.openPalette()}
                  >
                    ${icons.search}
                  </button>
                </openclaw-tooltip>
                ${homePanelAvailable ? renderCollapsedHomeToggle() : nothing}
              </div>
            `
          : nothing
      }
      ${
        nativeEmbed
          ? nothing
          : html`<button
                type="button"
                class="shell-nav-backdrop"
                tabindex="-1"
                aria-hidden="true"
                ?inert=${!navDrawerOpen}
                @click=${() => host.closeNavDrawer({ restoreFocus: true })}
              ></button>
              <div
                class="shell-nav ${mobileNavLayout ? "nav-drawer" : ""}"
                role=${mobileNavLayout ? "dialog" : nothing}
                aria-modal=${mobileNavLayout && navDrawerOpen ? "true" : nothing}
                aria-label=${mobileNavLayout ? t("palette.categories.navigation") : nothing}
                aria-hidden=${mobileNavLayout && navigationSurfaceHidden ? "true" : nothing}
                tabindex=${mobileNavLayout ? -1 : nothing}
                ?inert=${navigationSurfaceHidden}
              >
                ${navigationContent}
              </div>`
      }
      ${
        !nativeEmbed && !navCollapsed && !onboarding && !settingsTakeover
          ? html`
              <resizable-divider
                class="sidebar-resizer"
                .label=${t("nav.resize")}
                .splitRatio=${navigationSnapshot.navWidth / shellWidth}
                .minRatio=${NAV_WIDTH_MIN / shellWidth}
                .maxRatio=${NAV_WIDTH_MAX / shellWidth}
                aria-valuetext=${`${navigationSnapshot.navWidth} pixels`}
                title=${t("nav.resize")}
                @resize=${(event: CustomEvent<{ splitRatio: number }>) =>
                  host.resizeNavigation(event.detail.splitRatio)}
              ></resizable-divider>
            `
          : nothing
      }
      <main
        id="control-ui-main"
        class="content ${chatLikeRoute ? "content--chat" : ""} ${
          activeRoute === "custodian" ? "content--custodian" : ""
        } ${activeRoute === "workboard" ? "content--workboard" : ""}"
        .tabIndex=${-1}
        @mousedown=${beginNativeWindowDragFromTopInset}
        ?inert=${(!nativeEmbed && pageActionsBlocked) || (mobileNavLayout && navDrawerOpen)}
      >
        ${
          pageActionsBlocked
            ? html`<div class="connection-action-block" role="status" aria-live="polite">
                <span class="connection-action-block__icon" aria-hidden="true"
                  >${icons.globeOff}</span
                >
                <span class="connection-action-block__text">
                  ${t(
                    settingsTakeover
                      ? "connection.settingsChangesUnavailable"
                      : "connection.actionsUnavailable",
                  )}
                </span>
              </div>`
            : nothing
        }
        ${renderFloatingUpdateCard({
          navigationSurfaceHidden,
          mobileNavLayout,
          onboarding,
          compact: mergedChatChrome && !controlUiRefreshRequired,
          updateAvailable: overlaySnapshot.updateAvailable,
          updateSchedule: overlaySnapshot.updateSchedule,
          heldUpdateCampaignId: overlaySnapshot.heldUpdateCampaignId,
          updateBusy,
          statusBanner: overlaySnapshot.updateStatusBanner,
          updateRun: overlaySnapshot.updateRun,
          updateRunAcknowledged: overlaySnapshot.updateRunAcknowledged,
          connected: gatewayConnected,
          onAcknowledge: () => context.overlays.acknowledgeUpdateRun(),
          onCheckStatus: () => context.overlays.refreshUpdateStatus(),
          watchUpdateProgress,
          canUpdate,
          canHoldUpdate,
          onUpdate: () => void context.overlays.runUpdate(),
          refreshRequired: controlUiRefreshRequired,
          onRefresh: host.refreshControlUi,
          onHoldUpdate: () => context.overlays.holdUpdate(),
          onReviewUpdate: () => host.navigate("updates"),
          onNavigate: (routeId, options) => host.navigate(routeId, options),
          onOpenApprovals: () => host.openApprovals(),
        })}
        ${nativeEmbed ? navigationContent : nothing}
        <openclaw-router-outlet
          ?inert=${pageActionsBlocked || reloadRequired}
          aria-disabled=${pageActionsBlocked || reloadRequired ? "true" : nothing}
          .router=${runtime.router}
          .retryContext=${context}
          .retentionScope=${presentationScope}
          .onNotFound=${host.recoverNotFoundRoute}
          .notFoundRecoveryReady=${gatewayConnected}
        ></openclaw-router-outlet>
      </main>
      ${
        (navigationSurfaceHidden ||
          (settingsTakeover
            ? host.settingsSidebarRenderer === null
            : !isOptionalElementDefined(APP_SIDEBAR_ELEMENT))) &&
        !nativeEmbed &&
        !onboarding &&
        connectionStatus &&
        !initialConnection
          ? html`<div class="shell-connection-status">
              ${renderGatewayStatus({
                kind: connectionStatus,
                lastError: gatewaySnapshot.lastError,
                onRetry: () => context.gateway.connect(),
              })}
            </div>`
          : nothing
      }
      <openclaw-terminal-panel
        ?inert=${navDrawerOpen}
        .client=${gatewayConnected ? gatewaySnapshot.client : null}
        .available=${terminalAvailable}
        .agentId=${selectedAgentId}
        .sessionKey=${sessionRoute ? host.activeSessionKey : null}
        .suppressed=${settingsTakeover || nativeEmbed}
        .themeMode=${context.theme.resolvedMode}
        .basePath=${context.basePath}
      ></openclaw-terminal-panel>
      ${sessionRoute ? nothing : renderShellDocks(context, navDrawerOpen, settingsTakeover || nativeEmbed, selectedAgentId, activeRoute)}
      <openclaw-assistant-panel
        ?inert=${navDrawerOpen}
        .custodianAvailable=${custodianPanelAvailable && !nativeEmbed}
        .homeAvailable=${homePanelAvailable && !nativeEmbed}
        .custodianSuppressed=${activeRoute === "custodian"}
        .pageSessionKey=${host.activeSessionKey}
        .pageAgentId=${selectedAgentId}
        .pageRouteId=${activeRoute}
        .pageRouteFailed=${host.routeState.routeFailed === true}
        .minimizeRequestId=${host.custodianMinimizeRequestId}
      ></openclaw-assistant-panel>
      ${
        isOptionalElementDefined(host.execApprovalElement)
          ? html`<openclaw-exec-approval
              .props=${{
                queue: overlaySnapshot.approvalQueue,
                busy: overlaySnapshot.approvalBusy,
                canGrant: overlaySnapshot.approvalCanGrant,
                errors: overlaySnapshot.approvalErrors,
                onDecision: (
                  approvalId: string,
                  decision: Parameters<typeof context.overlays.decideApproval>[0],
                ) => context.overlays.decideApproval(decision, approvalId),
              }}
            ></openclaw-exec-approval>`
          : nothing
      }
      ${renderLazyDevicePairSetup(host, {
        open: overlaySnapshot.devicePairSetupOpen,
        lifecycle: overlaySnapshot.devicePairSetupLifecycle,
        nowMs: Date.now(),
        pendingCount: overlaySnapshot.devicePairPendingCount,
        onRefresh: () => void context.overlays.refreshDevicePairSetup(),
        onAccessChange: (access) => void context.overlays.setDevicePairSetupAccess(access),
        onClose: () => context.overlays.closeDevicePairSetup(),
        onManageDevices: () => {
          context.overlays.closeDevicePairSetup();
          host.navigate("devices");
        },
        onGetApps: () => {
          context.overlays.closeDevicePairSetup();
          host.navigate("apps");
        },
      })}
      ${
        memoryImportActive && isOptionalElementDefined(host.onboardingMemoryImportElement)
          ? html`<openclaw-onboarding-memory-import
              .active=${true}
              .context=${context}
            ></openclaw-onboarding-memory-import>`
          : nothing
      }
      <openclaw-toast-host></openclaw-toast-host>
    </div>
  `;
  // Keep plugin settings reachable when a replacement owns the workspace.
  if (activeRoute === "plugins") {
    return workspace;
  }
  return renderPluginSurface(
    "workspace",
    {
      sessionKey: host.activeSessionKey,
      agentId: resolveUiSelectedSessionAgentId(
        {
          assistantAgentId:
            context.agentSelection.state.selectedId ?? gatewaySnapshot.assistantAgentId,
          agentsList: context.agents.state.agentsList,
          hello: gatewaySnapshot.hello,
        },
        host.activeSessionKey,
      ),
      routeId: activeRoute,
    },
    workspace,
  );
}
